<template>
  <!-- Full-bleed band: white on mobile, stone-75 from `md:` up. The content
       column is capped at 820px. -->
  <div class="bg-white md:bg-stone-75 section-y">
    <div class="max-w-[820px] m-auto px-4">
      <h2
        v-if="content?.title"
        class="section-title text-center text-stone-900 mb-8 md:mb-10"
      >
        {{ content.title }}
      </h2>
      <p
        v-if="content?.subtitle"
        class="text-caption md:text-body text-center text-stone-800 mb-5 md:mb-6"
      >
        {{ content.subtitle }}
      </p>

      <div class="flex flex-col gap-3">
        <Disclosure
          v-for="(item, index) in questions"
          :key="item?.id"
          v-slot="{ open }"
          as="div"
          class="bg-white rounded-lg px-4 py-4 md:px-5 border-gray-200 border"
          :default-open="index === 0"
        >
          <DisclosureButton
            as="div"
            class="flex w-full items-center justify-between gap-4 text-left cursor-pointer"
          >
            <!-- Arbitrary sizes, not the text-h6/text-label tokens: those bake
                 a font-weight that would override the explicit weights below. -->
            <span
              class="text-[1.25rem] md:text-[0.875rem] font-semibold md:font-medium text-stone-900 leading-snug"
            >
              {{ item?.question }}
            </span>
            <!-- Plus/minus from `md:` up, chevrons on mobile — as designed. -->
            <component
              :is="open ? Minus : Plus"
              class="hidden md:block size-4 shrink-0 text-gray-500"
              aria-hidden="true"
            />
            <component
              :is="open ? ChevronUp : ChevronDown"
              class="md:hidden size-5 shrink-0 text-gray-500"
              aria-hidden="true"
            />
          </DisclosureButton>
          <DisclosurePanel
            as="div"
            class="text-caption md:text-body text-stone-800 mt-2 leading-snug md:leading-6"
          >
            <div class="faq-answer" v-html="item?.answer" />
          </DisclosurePanel>
        </Disclosure>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { ChevronDown, ChevronUp, Minus, Plus } from 'lucide-vue-next'
import { parseFaqAnswer } from '~/utils/faqMarkdown'

const { content = {} } = defineProps<{ content: any }>()

// Answers are markdown from the CMS — render links, lists and paragraphs
const questions = computed(() =>
  content?.questions?.map((item) => ({
    ...item,
    answer: parseFaqAnswer(item?.answer),
  }))
)
</script>

<style lang="css" scoped>
@reference '~/assets/css/tailwind.css';

.faq-answer {
  :deep(p),
  :deep(ul),
  :deep(ol) {
    @apply mb-2 last:mb-0;
  }

  :deep(ul) {
    @apply list-disc pl-5;
  }

  :deep(ol) {
    @apply list-decimal pl-5;
  }

  /* match the magazine article body's link style */
  :deep(a) {
    @apply text-teal-800 hover:underline duration-300 ease-in-out;
  }
}
</style>
